// frontend/src/pages/admin/TicketPriorityForm.js
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Button, InputNumber } from 'antd';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';

const TicketPriorityForm = ({ visible, onCancel, onSuccess, priority }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const notificationApi = useNotificationApi();

  // Helper functions for notifications
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((messageContent, key = 'globalLoading') => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  // Effect to populate form fields
  useEffect(() => {
    if (visible) {
      setLoading(false);
      if (priority) {
        form.setFieldsValue({
          name: priority.name,
          sortOrder: priority.sortOrder,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          sortOrder: 10
        });
      }
    } else {
      form.resetFields();
    }
  }, [visible, priority, form]);

  const handleSubmit = async () => {
    let hideLoading;
    try {
      setLoading(true);
      hideLoading = showLoading(priority ? 'Updating priority...' : 'Adding priority...', 'priorityFormLoading');

      const values = await form.validateFields();
      const payload = {
        name: values.name,
        sort_order: values.sortOrder,
      };

      if (priority) {
        await api.put(`/api/priorities/${priority.id}`, payload);
        showSuccess('Priority Updated', `Priority "${values.name}" updated successfully!`, 'priorityUpdateSuccess');
      } else {
        await api.post('/api/priorities', payload);
        showSuccess('Priority Created', `New priority "${values.name}" created successfully!`, 'priorityCreateSuccess');
      }

      setTimeout(() => {
        if (hideLoading && typeof hideLoading === 'function') hideLoading();
        onSuccess(true);
        onCancel();
      }, 500);

    } catch (error) {
      if (hideLoading && typeof hideLoading === 'function') hideLoading();
      setLoading(false);
      console.error('Priority form submission error:', error.response?.data || error.message, error);
      showError('Submission Failed', error.response?.data?.message || 'Failed to save priority.', 'prioritySubmitError');
    }
  };

  return (
    <Modal
      open={visible}
      title={priority ? 'Edit Priority' : 'Add New Priority'}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={600}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        name="priority_form"
        initialValues={{ sortOrder: 10 }}
      >
        <Form.Item
          name="name"
          label="Priority Name"
          rules={[
            { required: true, message: 'Please input the priority name!' },
            { type: 'string', min: 2, message: 'Priority name must be at least 2 characters.' }
          ]}
        >
          <Input placeholder="e.g., High, Medium, Low" />
        </Form.Item>

        <Form.Item
          name="sortOrder"
          label="Sort Order"
          rules={[
            { required: true, message: 'Please input the sort order!' },
            { type: 'number', min: 0, message: 'Sort order must be a non-negative number!' },
          ]}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g., 10" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TicketPriorityForm;